import { push } from "connected-react-router";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { NavLink, withRouter, useLocation } from "react-router-dom";
import styled from "styled-components";
import { signoutUser } from "../../actions";
import { getContrastColor, translucentColor } from "../../utils";
import { fetchNotificationGroups } from "../../actions/notification_groups";
import { getUnapprovedInventoryItemsCountAJAX } from "../../actions/inventory_items";
import { ChatGptTrigger } from "@rpforms/web/src/components/chatgpt/ChatGptTrigger";
import { SearchInput } from "./SearchInput";
import { SET_SEARCH_QUERY, SEARCH_FOLDERS_QUERY } from "../../actions/types";

const NavbarWrapper = styled.div`
    .navbar {
        display: flex;
        font-weight: bold;
        justify-content: space-between;

        &.navbar-lead {
            background: linear-gradient(
                to bottom,
                ${(props) => translucentColor(props.theme.primary_color || "#00bcd4", 0.8)},
                ${(props) => props.theme.primary_color || "#00bcd4"}
            );

            font-size: 24px;
            font-family: "Merriweather", serif;
            color: ${(props) =>
                getContrastColor(props.theme.primary_color || "#00bcd4")} !important;
        }

        .text-link {
            position: relative;
            top: -10px;
        }
    }
    a {
        transition: all 0.2s ease-in-out;
    }

    a:hover {
        color: #fff;
        transform: scale(1.2);
    }
`;
const SubnavWrapper = styled.nav`
    padding: 20px 40px 0;
    background: ${(props) => props.theme.primary_color || "#00bcd4"} !important;

    ul {
        width: 100%;
        display: flex;
        margin: 0;
        padding: 0;
        list-style: none;

        li {
            flex-grow: 1;
            margin: 0;
            color: ${(props) => getContrastColor(props.theme.primary_color || "#00bcd4")};
            text-align: center;

            a {
                display: block;
                color: ${(props) => getContrastColor(props.theme.primary_color || "#00bcd4")};
                border-radius: 10px 10px 0 0;
                padding: 10px 5px 5px;
                text-decoration: none;
                transition: 0.2s ease-in-out;
                transition-property: transform, color, background-color, box-shadow;
                margin: 0 20px;
                font-size: 16px;

                &:hover,
                &.active {
                    transform: scale(1.2);
                }

                &.active,
                &.active-without-shadow {
                    background: #fff;
                    color: ${(props) => props.theme.primary_color || "#00bcd4"};
                    cursor: pointer;
                    box-shadow: 0 35px 8px 8px rgba(0, 0, 0, 0.3);
                    text-decoration: none;
                }
                &.active-without-shadow {
                    box-shadow: none !important;
                }
            }

            i {
                display: block;
                font-size: 20px;
            }

            font-weight: 300;
        }
    }
`;

const CountBadgeAnchor = styled.span`
    position: relative;
    display: inline-block;
`;

const CountBadge = styled.span`
    position: absolute;
    top: -6px;
    right: -14px;
    min-width: 18px;
    height: 18px;
    padding: 0 5px;
    border-radius: 9px;
    background: #dc3545;
    color: #fff;
    font-size: 11px;
    font-weight: bold;
    line-height: 18px;
    text-align: center;
`;

const IconContainer = styled.div`
    display: flex;
    justify-content: space-around;
    align-items: center;
    min-width: 150px;
    gap: 20px;
`;

export const Navbar = () => {
    const auth = useSelector<any, any>((state) => state.auth);
    const company = useSelector<any, any>((state) => state.company);
    const dispatch = useDispatch();
    const notificationGroups = useSelector<any, any>((state) => state.notificationGroups);
    const location = useLocation();

    useEffect(() => {
        if (!auth.authenticated) {
            return;
        }
        dispatch((fetchNotificationGroups as any)(1));
    }, [auth.authenticated]);

    const inventoryItemsData = useSelector<any, any>((state) => state.inventoryItemReducer?.data);
    const [unapprovedInventoryCount, setUnapprovedInventoryCount] = useState(0);

    useEffect(() => {
        if (!auth.authenticated) {
            return undefined;
        }
        let isActive = true;
        getUnapprovedInventoryItemsCountAJAX()
            .then((count) => {
                if (isActive) {
                    setUnapprovedInventoryCount(count);
                }
            })
            .catch(() => undefined);
        return () => {
            isActive = false;
        };
    }, [auth.authenticated, location.pathname, inventoryItemsData]);

    let currentSearch = useSelector<any, any>((state) => state.search.query);
    try {
        currentSearch = decodeURIComponent(currentSearch);
    } catch (e) {}

    const signout = () => {
        dispatch(signoutUser());
    };

    const clearSearch = () => {
        dispatch({
            type: SET_SEARCH_QUERY,
            payload: "",
        });
        dispatch({
            type: SEARCH_FOLDERS_QUERY,
            payload: {
                query: "",
                scope: "folder",
            },
        });
    };

    const handleSearchChange = (searchTerm: string) => {
        if (!searchTerm) {
            clearSearch();
            if (location.pathname !== "/entries") {
                dispatch(push("/entries"));
            }
            return;
        }

        const target = `/search/${encodeURIComponent(searchTerm)}`;
        if (location.pathname !== target) {
            dispatch(push(target));
        }
    };

    return (
        <NavbarWrapper className={"main-nav"}>
            <nav className="navbar navbar-light text-white navbar-lead">
                {company.name}
                <SearchInput
                    value={currentSearch}
                    placeholder="Suche..."
                    onSearchChange={handleSearchChange}
                />
                {/*
                Entfernt für scriptfeld.
                <form onSubmit={onSearchSubmit}>*/}
                {/*</form>*/}
                <IconContainer>
                    <NavLink
                        to="/notifications/all"
                        style={{ position: "relative" }}
                        onClick={clearSearch}
                    >
                        <i className="fa fa-bell"></i>
                    </NavLink>
                    <a href="#" onClick={signout}>
                        <i className="fa fa-sign-out"></i>
                    </a>

                    <NavLink to="/settings/company/identity" onClick={clearSearch}>
                        <i className="fa fa-cog"></i>
                    </NavLink>
                </IconContainer>
            </nav>
            <SubnavWrapper className="navbar navbar-light bg-primary subnav">
                {auth.authenticated ? (
                    <ul>
                        <li data-engine="ipads">
                            <NavLink
                                to="/folders?ref=nav"
                                activeClassName="active"
                                onClick={clearSearch}
                            >
                                <i className="fa fa-folder"></i>
                                <span>Struktur</span>
                            </NavLink>
                        </li>
                        <li data-engine="viewer">
                            <NavLink to="/entries" activeClassName="active" onClick={clearSearch}>
                                <i className="fa fa-table"></i>
                                <span>Protokolle</span>
                            </NavLink>
                        </li>
                        <li data-engine="finances">
                            <NavLink to="/finances" activeClassName="active" onClick={clearSearch}>
                                <i className="fa fa-dollar"></i>
                                <span>Finanzen</span>
                            </NavLink>
                        </li>
                        <li data-engine="calendar">
                            <NavLink to="/calendar" activeClassName="active" onClick={clearSearch}>
                                <i className="fa fa-calendar"></i>
                                <span>Terminplaner</span>
                            </NavLink>
                        </li>
                        <li data-engine="master_data">
                            <NavLink
                                to="/master_data"
                                activeClassName="active"
                                onClick={clearSearch}
                            >
                                <i className="fa fa-database"></i>
                                <span>Stammdaten</span>
                            </NavLink>
                        </li>
                        <li data-engine="inventory_items">
                            <NavLink
                                to="/inventory_items/repair"
                                activeClassName="active"
                                onClick={clearSearch}
                            >
                                <CountBadgeAnchor>
                                    <i className="fa fa-fire-extinguisher"></i>
                                    {unapprovedInventoryCount > 0 && (
                                        <CountBadge title="Nicht freigegebene Positionen">
                                            {unapprovedInventoryCount}
                                        </CountBadge>
                                    )}
                                </CountBadgeAnchor>
                                <span>Leistungsverzeichnis</span>
                            </NavLink>
                        </li>
                    </ul>
                ) : (
                    <NavLink
                        className="text-link text-white"
                        to="/auth/login"
                        activeClassName="active"
                    >
                        <i className="fa fa-key pr-1"></i>
                        <span>Anmelden</span>
                    </NavLink>
                )}
            </SubnavWrapper>
        </NavbarWrapper>
    );
};

export default withRouter(Navbar);
